/**
 * UNIVAPLAT-3689: query (read) models for the admin organisation endpoints
 * (`/api/v2/organisations`, backend contract UNIVAPLAT-3665).
 *
 * Staff names, emails and change reasons are personal data: never log them (console, Sentry
 * breadcrumbs) and never store them in localStorage.
 */

/** Minimal organisation used by the dashboard filter and patient registration (`getAll`). */
export interface Organisation {
    id: number,
    name: string,
}

/** Contract summary embedded in a list item. */
export interface OrganisationListContract {
    id: number,
    title: string,
    code: string,
}

/** Fields common to the list item and the detail response. Dates are DATE_ATOM strings. */
interface OrganisationBase {
    id: number,
    name: string | null,
    code: string,
    address: string | null,
    city: string | null,
    postcode: string | null,
    /** ISO string when deactivated, otherwise null. */
    disabledAt: string | null,
    /** ISO string when deleted, otherwise null. */
    deletedAt: string | null,
}

/** `GET /api/v2/organisations` item. */
export interface OrganisationListItem extends OrganisationBase {
    contracts: OrganisationListContract[],
}

/** `GET /api/v2/organisations/{id}`. Linked contracts and services are not embedded: use their own lists. */
export type OrganisationDetail = OrganisationBase;

/** `GET /api/v2/organisations/{id}/contracts` item. */
export interface OrganisationLinkedContract {
    id: number,
    title: string,
    code: string,
    serviceProvider: string | null,
    signatureDate: string | null,
    kickoffDate: string | null,
    disabledAt: string | null,
    deletedAt: string | null,
}

/** `GET /api/v2/organisations/{id}/care-programs` item. */
export interface OrganisationCareProgram {
    id: number,
    title: string,
    scope: string,
    target: string,
    durationInWeeks: number,
    numberOfModules: number,
    addedOn: string | null,
}

/** `GET /api/v2/organisations/{id}/services` item. */
export interface OrganisationLinkedService {
    id: number,
    name: string,
    address: string | null,
    city: string | null,
    postcode: string | null,
    disabledAt: string | null,
    addedOn: string | null,
}

/** `GET /api/v2/organisations/{id}/admins` item. `id` is the organisation-admin link id (used to remove it), not the user id. */
export interface OrganisationAdminListItem {
    id: number,
    firstName: string | null,
    lastName: string,
    email: string | null,
    addedOn: string,
}

export type OrganisationLogAction = 'deactivate' | 'reactivate' | 'delete' | 'link_care_program' | 'unlink_care_program';

/** `GET /api/v2/organisations/{id}/logs` item. `careProgram` is set for the link and unlink actions. */
export interface OrganisationLogEntry {
    id: number,
    action: OrganisationLogAction,
    reason: string | null,
    createdAt: string,
    careProgram?: { id: number, title: string } | null,
}

/** `GET /api/v2/contracts/{id}/organisations` item. */
export interface ContractLinkedOrganisation {
    id: number,
    name: string | null,
    code: string,
    address: string | null,
    city: string | null,
    postcode: string | null,
    disabledAt: string | null,
    deletedAt: string | null,
}

export interface OrganisationListFilters {
    name?: string,
    code?: string,
    address?: string,
    city?: string,
    postcode?: string,
    showDeactivated?: boolean,
    showDeleted?: boolean,
}

/** Query of the care programme and service lists: `linked: false` lists the entries still available to link. */
export interface OrganisationLinkedQuery {
    linked?: boolean,
}

/** Query of `GET /api/v2/contracts/{id}/organisations`. `linked: false` is paginated and filterable. */
export interface ContractOrganisationsQuery extends OrganisationLinkedQuery {
    name?: string,
    code?: string,
}

export function isOrganisationDeleted(organisation: Pick<OrganisationBase, 'deletedAt'>): boolean {
    return !!organisation.deletedAt;
}

export function isOrganisationDeactivated(organisation: Pick<OrganisationBase, 'disabledAt' | 'deletedAt'>): boolean {
    return !!organisation.disabledAt;
}

export function isOrganisationActive(organisation: Pick<OrganisationBase, 'disabledAt' | 'deletedAt'>): boolean {
    return !organisation.disabledAt && !organisation.deletedAt;
}
